import React from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  CaseStatus,
  Image,
  ImageAndQueries,
  CaseWithImages,
  QueryStatus,
  UUID
} from "../../models";
import { toggleImageSelectorExpanded } from "../../slices/imageSelector";
import Expandable from "../Expandable";
import { Text, Radio } from "@blasterjs/core";
import { faFileArrowDown, faCaretUp, faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { AppDispatch } from "../../store";
import { openDownloadImageDialog } from "../../slices/downloadImageDialog";
import { cancelImageAnnotation, reorderImages, selectImage } from "../../slices/caseImageViewer";
import { RolePermissions } from "../../permissions";
import DownloadImageDialog from "../DownloadImageDialog";
import { compareImages, moveOrderUnit } from "../../utils";

const orderLockedTooltip =
  "Image order can only be changed while the case is Pending Lab QC or Pending QC.";

interface ReorderProps {
  readonly canReorder: boolean;
  readonly orderLocked: boolean;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly onMoveUp: () => void;
  readonly onMoveDown: () => void;
}

const ReorderContainer = styled.div`
  width: 1.75rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

// The up/down arrows, shared by image rows and (LUC-305) biopsy group headers.
const ReorderArrows = ({ reorder }: { readonly reorder: ReorderProps }) => {
  const upDisabled = reorder.orderLocked || reorder.isFirst;
  const downDisabled = reorder.orderLocked || reorder.isLast;
  const arrowTitle = (disabled: boolean, action: string): string =>
    reorder.orderLocked ? orderLockedTooltip : disabled ? "" : action;
  return reorder.canReorder ? (
    <ReorderContainer>
      <FontAwesomeIcon
        icon={faCaretUp}
        title={arrowTitle(upDisabled, "Move up")}
        style={{
          width: "1.5rem",
          height: "1.5rem",
          color: upDisabled ? "#9A9DA0" : "#555",
          cursor: upDisabled ? "not-allowed" : "pointer"
        }}
        onClick={upDisabled ? undefined : reorder.onMoveUp}
      />
      <FontAwesomeIcon
        icon={faCaretDown}
        title={arrowTitle(downDisabled, "Move down")}
        style={{
          width: "1.5rem",
          height: "1.5rem",
          color: downDisabled ? "#9A9DA0" : "#555",
          cursor: downDisabled ? "not-allowed" : "pointer"
        }}
        onClick={downDisabled ? undefined : reorder.onMoveDown}
      />
    </ReorderContainer>
  ) : null;
};

interface ImageListItemProps {
  readonly image: Image;
  readonly index: number;
  readonly isShownToReader?: boolean;
  readonly imageQueries?: ImageAndQueries;
  readonly reorder?: ReorderProps;
  // FE-05: when the image belongs to a biopsy group, the radio label is the biopsy-relative
  // "Image N" (regardless of sort order); the group's prefix is shown in the header above. When
  // absent, the row renders the image's own name (non-grouped, as today).
  readonly displayLabel?: string;
}

const ImageListItem = ({
  image,
  index,
  isShownToReader = true,
  imageQueries,
  reorder,
  displayLabel
}: ImageListItemProps) => {
  const dispatch = useAppDispatch();

  const loggedInUser = useAppSelector(state => state.auth.loggedInUser);

  const userCanDownloadImages =
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.IM_ImagesListTab_DownloadImages]);

  const selectedImage = useAppSelector(state => state.caseImageViewer.imageWithAnnotations);

  const downloadImage = (image: Image, dispatch: AppDispatch) => {
    dispatch(openDownloadImageDialog(image));
  };

  const onSelectImage = (image: Image, dispatch: AppDispatch) => async () => {
    // NOTE: It's important to dispatch this action to cancel any in-progress annotation to ensure the
    // annotation code cleans up after itself properly.
    dispatch(cancelImageAnnotation());
    await dispatch(selectImage(image.id));
  };

  const BiopsyLocationText = styled.label`
    color: #ccc;
  `;

  const ImageHiddenFromReaderLabel = styled.span`
    color: gray;
  `;

  const ImageContainer = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    width: 100%;
  `;

  const RadioContainer = styled.div`
    width: 2.5rem;
    flex-shrink: 0;
    border-left: 2px solid transparent;
  `;

  const ResolvedRadioContainer = styled.div`
    width: 2.5rem;
    flex-shrink: 0;
    border-left: 2px solid gray;
  `;

  const UnresolvedRadioContainer = styled.div`
    width: 2.5rem;
    flex-shrink: 0;
    border-left: 2px solid #dc3831;
  `;

  const ContentContainer = styled.div`
    flex: 1;
  `;

  const DownloadContainer = styled.div`
    width: 2rem;
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    padding-top: 0.2rem;
  `;

  const RadioLabel = styled.label`
    cursor: pointer;
  `;

  const radioId = `ImageSelector-${index}`;

  function imageQueriesWereClosed(imageQueries: ImageAndQueries): boolean {
    return imageQueries
      .map(imageAndQuery => imageAndQuery.lastLabQuery)
      .some(query => query && query.queryStatus == QueryStatus.Closed);
  }

  function imageQueriesNeedResolution(imageQueries: ImageAndQueries): boolean {
    return imageQueries
      .map(imageAndQuery => imageAndQuery.lastLabQuery)
      .some(query => query && query.queryStatus == QueryStatus.Open);
  }

  const RadioContainerComponent = imageQueries
    ? imageQueriesNeedResolution(imageQueries)
      ? UnresolvedRadioContainer
      : imageQueriesWereClosed(imageQueries)
      ? ResolvedRadioContainer
      : RadioContainer
    : RadioContainer;

  return (
    <li key={image.id}>
      <ImageContainer>
        {reorder && <ReorderArrows reorder={reorder} />}
        <RadioContainerComponent>
          <Radio
            id={radioId}
            checked={
              selectedImage !== null &&
              "resource" in selectedImage &&
              selectedImage.resource.imageAndQuery.image.id === image.id
            }
            onChange={onSelectImage(image, dispatch)}
          />
        </RadioContainerComponent>
        <ContentContainer>
          <RadioLabel htmlFor={radioId}>
            {isShownToReader ? (
              <Text>{displayLabel ?? image.name}</Text>
            ) : (
              <ImageHiddenFromReaderLabel>{displayLabel ?? image.name}</ImageHiddenFromReaderLabel>
            )}
            <br />
            <BiopsyLocationText>{image.biopsyLocation}</BiopsyLocationText>
          </RadioLabel>
        </ContentContainer>
        {userCanDownloadImages && (
          <DownloadContainer>
            <FontAwesomeIcon
              icon={faFileArrowDown}
              style={{
                width: "1.5rem",
                height: "1.5rem",
                color: "#9A9DA0",
                cursor: "pointer",
                transform: "scale(1.2)"
              }}
              onClick={() => downloadImage(image, dispatch)}
              title="Download Image"
            />
          </DownloadContainer>
        )}
      </ImageContainer>
    </li>
  );
};

interface Props {
  readonly histoCase: CaseWithImages;
}

const ImageSelector = ({ histoCase }: Props) => {
  const dispatch = useAppDispatch();
  const isImageSelectorPanelExpanded = useAppSelector(
    state => state.imageSelector.isImageSelectorPanelExpanded
  );
  const selectedImage = useAppSelector(state => state.caseImageViewer.imageWithAnnotations);
  const loggedInUser = useAppSelector(state => state.auth.loggedInUser);
  // FE-05: biopsy groupings for this case (BE-03), fetched from the backend by the case viewer.
  const biopsies = useAppSelector(state => state.caseImageViewer.biopsies);

  // Images may be reordered in-place by ISC/StudyAdmin/SystemAdmin, but only while the overall
  // case status is Pending Lab QC or Pending QC (regardless of whether Central Readers are
  // assigned). Mirrors the backend CaseServices.reorderImages check.
  const canReorder =
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.AP_CaseViewer_ReorderImages]);
  const orderLocked = ![CaseStatus.PendingLabQC, CaseStatus.PendingQC].includes(
    histoCase.caseWithStatus.status
  );

  // LUC-305: sort order, natural segment, name, then id (mirrors backend ImageOrdering).
  const orderedImages: ReadonlyArray<Image> = [...histoCase.images].sort(compareImages);
  const positionById: ReadonlyMap<UUID, number> = new Map(
    orderedImages.map((image, idx) => [image.id, idx])
  );

  const selectedImageIndex =
    selectedImage && "resource" in selectedImage
      ? "imageQueries" in histoCase
        ? histoCase.imageQueries.findIndex(
            imageQuery => imageQuery[0]?.image.id === selectedImage.resource.imageAndQuery.image.id
          ) + 1
        : histoCase.images.findIndex(
            image => image.id === selectedImage.resource.imageAndQuery.image.id
          ) + 1
      : 0;

  // FE-05: biopsy groupings come from the backend (BE-03, single source of truth), then map the
  // ordered image ids to this case's image objects. `groupingApplies` is true only when at least one
  // real multi-image group exists; otherwise the list renders flat (as today).
  const imagesById: ReadonlyMap<UUID, Image> = new Map(
    histoCase.images.map(image => [image.id, image])
  );
  const biopsyGroups = biopsies.map(biopsy => ({
    name: biopsy.name,
    isGroup: biopsy.isGroup,
    images: biopsy.imageIds
      .map(id => imagesById.get(id))
      .filter((image): image is Image => image !== undefined)
  }));
  const groupingApplies = biopsyGroups.some(group => group.isGroup);

  // LUC-305: the units the arrows move. With grouping, a multi-image biopsy is one block, so an
  // arrow moves a row (or a whole group) exactly one place in the DISPLAYED list — past a whole
  // neighbouring group rather than into it. Any case image missing from the biopsy list (e.g. not
  // yet re-fetched) is kept as its own unit at the end. Without grouping, every image is a unit.
  const groupedImageIds: ReadonlySet<UUID> = new Set(
    biopsyGroups.flatMap(group => group.images.map(image => image.id))
  );
  const orderUnits: ReadonlyArray<ReadonlyArray<Image>> = groupingApplies
    ? [
        ...biopsyGroups.map(group => group.images).filter(images => images.length > 0),
        ...orderedImages.filter(image => !groupedImageIds.has(image.id)).map(image => [image])
      ]
    : orderedImages.map(image => [image]);
  const unitIndexById: ReadonlyMap<UUID, number> = new Map(
    orderUnits.flatMap((unit, unitIndex) => unit.map(image => [image.id, unitIndex] as const))
  );

  const handleMove = (unitIndex: number, direction: 1 | -1) => {
    const imageOrders = moveOrderUnit(orderUnits, unitIndex, direction);
    if (imageOrders !== null) {
      void dispatch(reorderImages({ caseId: histoCase.caseWithStatus.id, imageOrders }));
    }
  };

  const reorderPropsForUnit = (unitIndex: number): ReorderProps => ({
    canReorder,
    orderLocked,
    isFirst: unitIndex <= 0,
    isLast: unitIndex < 0 || unitIndex >= orderUnits.length - 1,
    onMoveUp: () => handleMove(unitIndex, -1),
    onMoveDown: () => handleMove(unitIndex, 1)
  });
  const reorderPropsFor = (image: Image): ReorderProps =>
    reorderPropsForUnit(unitIndexById.get(image.id) ?? -1);

  const flatImageQueries: ImageAndQueries =
    "imageQueries" in histoCase ? histoCase.imageQueries.flat() : [];

  // Render one image row, computing its query/reorder props the same way the flat list does.
  // `displayLabel` overrides the row label ("Image N" within a biopsy group). Reorder controls are
  // suppressed on rows inside a multi-image biopsy (`inGroup`): the group moves as one block via the
  // arrows on its header (LUC-305). Single-image biopsies stay reorderable.
  const renderImageRow = (image: Image, displayLabel?: string, inGroup = false) => {
    const imageAndQuery = flatImageQueries.find(iaq => iaq.image.id === image.id);
    const imageQueriesForImage = flatImageQueries.filter(
      q => q.lastLabQuery?.objectId === image.id
    );
    return ImageListItem({
      image,
      index: positionById.get(image.id) ?? 0,
      isShownToReader: imageAndQuery?.isShownToReader,
      imageQueries: imageQueriesForImage.length > 0 ? imageQueriesForImage : undefined,
      reorder: canReorder && !inGroup ? reorderPropsFor(image) : undefined,
      displayLabel
    });
  };

  const BiopsyHeader = styled.li`
    font-weight: 700;
    margin-top: 0.5rem;
    list-style: none;
    display: flex;
    align-items: center;
    gap: 1rem;
  `;

  const BiopsyGroupItems = styled.ul`
    padding-left: 1.5rem;
    list-style: none;
  `;

  const ImageSelectorPanel = styled.div`
    display: flex;
    margin: 15px;
    padding: 0;
    z-index: 30000;
    left: 15px;
    top: 15px;
    box-shadow: 0 0 0 1px rgba(16, 22, 26, 0.1), 0 0 0 rgba(16, 22, 26, 0),
      0 1px 1px rgba(16, 22, 26, 0.2);
    width: fit-content;
  `;

  const ImageList = styled.ul`
    padding: 10px;
    max-height: 50vh;
    overflow-y: auto;
  `;

  const imageCount =
    "imageQueries" in histoCase ? histoCase.imageQueries.length : histoCase.images.length;
  const title = `Images (${selectedImageIndex}/${imageCount})`;

  return (
    <ImageSelectorPanel>
      <Expandable
        label={title}
        isExpanded={isImageSelectorPanelExpanded}
        onToggle={() => dispatch(toggleImageSelectorExpanded())}
        alwaysShowLabel={true}
      >
        <ImageList>
          {groupingApplies
            ? // FE-05: grouped display. A multi-image biopsy renders a <prefix> header with its
              // images indented and labelled "Image N" (N = position within the biopsy, regardless
              // of sort order). Single-image biopsies render flat, as today.
              biopsyGroups.map(group =>
                group.isGroup ? (
                  <React.Fragment key={`biopsy-${group.name}`}>
                    <BiopsyHeader>
                      {canReorder && group.images[0] ? (
                        <ReorderArrows
                          reorder={reorderPropsForUnit(unitIndexById.get(group.images[0].id) ?? -1)}
                        />
                      ) : null}
                      {group.name}
                    </BiopsyHeader>
                    <BiopsyGroupItems>
                      {group.images.map((image, idx) =>
                        renderImageRow(image, `Image ${idx + 1}`, true)
                      )}
                    </BiopsyGroupItems>
                  </React.Fragment>
                ) : (
                  <React.Fragment key={`single-${group.images[0]?.id ?? group.name}`}>
                    {group.images.map(image => renderImageRow(image))}
                  </React.Fragment>
                )
              )
            : "imageQueries" in histoCase && !canReorder
            ? histoCase.imageQueries.flatMap((imageQuery, queryIndex) =>
                imageQuery.map((imageAndQuery, imageIndex) =>
                  ImageListItem({
                    image: imageAndQuery.image,
                    index: queryIndex * 1000 + imageIndex,
                    isShownToReader: imageAndQuery.isShownToReader,
                    imageQueries: imageQuery.filter(
                      q => q.lastLabQuery?.objectId === imageAndQuery.image.id
                    ),
                    reorder: undefined
                  })
                )
              )
            : orderedImages.map(image => renderImageRow(image))}
        </ImageList>
      </Expandable>
      <DownloadImageDialog />
    </ImageSelectorPanel>
  );
};

export default ImageSelector;
